import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import {
  Home,
  LayoutList,
  LogOut,
  Menu,
  ScrollText,
  Search,
  UserCog,
  Users,
  Waypoints,
  X,
} from "lucide-react";
import { useRbac } from "@/context/RbacContext";
import { usePoolAccess } from "@/features/pool/hooks";
import type { Permission } from "@/api/types";
import type { TKeyStartingWith } from "@/i18n/keys";
import { setLang, SUPPORTED_LANGS, type Lang } from "@/i18n";
import { cmdKLabel } from "@/lib/platform";
import { cn } from "@/lib/cn";

interface NavItem {
  to: string;
  /**
   * A real translation key under `nav.`, not `string`. `string` here was the hole: it let any typo
   * reach `t()`, which then had nothing to check it against. Scoped to the `nav.` subtree rather than
   * the whole key union both because that is what a nav label IS, and because it keeps the key type a
   * small literal union.
   */
  labelKey: TKeyStartingWith<"nav.">;
  icon: ReactNode;
  /** Hidden unless the user holds this permission. */
  permission?: Permission;
}

const NAV: NavItem[] = [
  { to: "/", labelKey: "nav.home", icon: <Home className="size-[1.1rem]" /> },
  {
    to: "/worklists",
    labelKey: "nav.dashboard",
    icon: <LayoutList className="size-[1.1rem]" />,
    permission: "worklist:read",
  },
  {
    to: "/customers",
    labelKey: "nav.customers",
    icon: <Users className="size-[1.1rem]" />,
    permission: "customer:read",
  },
  {
    to: "/users",
    labelKey: "nav.users",
    icon: <UserCog className="size-[1.1rem]" />,
    permission: "user:manage",
  },
  {
    to: "/audit",
    labelKey: "nav.audit",
    icon: <ScrollText className="size-[1.1rem]" />,
    permission: "audit:read",
  },
  // No `permission` — the Pool module's gate is a SEPARATE server-side POOL ROLE, not an RBAC
  // permission, so it cannot be checked via hasPermission(). Visibility instead comes from
  // GET /pool/access (usePoolAccess, below) — a role-only item: a non-operator (`role: null`) never sees
  // it at all (see the `canSeePool` special-case in the filter below). The screens themselves still
  // render their own "not a Pool operator" state on a 403 (see PoolErrorState) as defense-in-depth — e.g.
  // a stale cached nav item for an operator whose access was just revoked.
  { to: "/pool", labelKey: "nav.pool", icon: <Waypoints className="size-[1.1rem]" /> },
];

export interface AppShellProps {
  children: ReactNode;
  /** Opens the omnibox (Cmd-K). Wired by RootLayout to the search overlay. */
  onOpenOmnibox?: () => void;
  onSignOut?: () => void;
}

/**
 * Sidebar shell: a LIGHT rail (brand + search + permission-gated nav + account) on the left, light
 * content on the right. The active nav item is a SOLID indigo pill — bold via the accent, not via a dark
 * surface, so the rail stays legible and on the light identity. The rail is fixed on desktop (lg+) and
 * collapses to an off-canvas drawer on smaller widths, so navigation + search stay reachable at EVERY
 * width. A skip link and a single <main> landmark keep keyboard + SR navigation clean.
 */
export function AppShell({ children, onOpenOmnibox, onSignOut }: AppShellProps) {
  const { t } = useTranslation();
  const { hasPermission, role } = useRbac();
  // Fires once per session (generous staleTime — see usePoolAccess) and never 403s, so this is safe to
  // call unconditionally on every render of the shell. `data` is undefined while pending AND on an
  // error (network/auth) — both fail CLOSED (`!== null`, not `=== null`), so the item never flashes in
  // before disappearing, and a transient failure hides it rather than showing it to someone unverified.
  const poolAccessQ = usePoolAccess();
  const poolRole = poolAccessQ.data?.role ?? null;
  // The portal Owner always sees Pool — even before the access query resolves — because rev 6 made
  // Owner's pool role IMPLICIT (no allow-list row at all, see PoolAccess): the fallback exists purely so
  // the nav item never flashes away for an Owner during a slow/errored /access call, not to route around
  // a real gate. Everyone else waits for a real non-null pool role (owner/admin/viewer) before Pool
  // appears. Data screens still 403 as normal for whatever the resolved role doesn't cover.
  const canSeePool = poolRole !== null || role === "owner";
  const visibleNav = NAV.filter((n) =>
    n.to === "/pool" ? canSeePool : !n.permission || hasPermission(n.permission),
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRef = useRef<HTMLElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Mobile drawer behaves as a modal dialog: focus moves to the close button on open, Tab is trapped
  // inside, Escape closes, and focus returns to the hamburger on close. (Desktop rail is always-on.)
  useEffect(() => {
    if (!drawerOpen) return;
    const hamburger = hamburgerRef.current;
    const focusables = () =>
      drawerRef.current
        ? Array.from(
            drawerRef.current.querySelectorAll<HTMLElement>(
              'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])',
            ),
          )
        : [];
    const raf = requestAnimationFrame(() => (closeButtonRef.current ?? focusables()[0])?.focus());
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setDrawerOpen(false);
        return;
      }
      if (e.key !== "Tab") return;
      const f = focusables();
      const first = f[0];
      const last = f[f.length - 1];
      // Nothing focusable inside the drawer (only reachable if drawerRef is already detached) → there is
      // no trap to enforce; let Tab behave natively instead of calling .focus() on undefined.
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKey);
      hamburger?.focus();
    };
  }, [drawerOpen]);

  return (
    <div className="min-h-dvh bg-(--bg)">
      <a
        href="#main"
        className={cn(
          "sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-(--z-tooltip) " +
            "focus:rounded-(--radius-sm) focus:bg-(--surface-raised) focus:px-3 focus:py-2 " +
            "focus:text-(length:--text-sm) focus:shadow-(--shadow-md)",
        )}
      >
        {t("shell.skipToContent")}
      </a>

      {/* Desktop rail — fixed, always visible at lg+. */}
      <aside
        className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-(--sidebar-border) bg-(--sidebar) lg:flex"
        style={{ zIndex: "var(--z-sticky)" }}
      >
        <SidebarContent
          visibleNav={visibleNav}
          onOpenOmnibox={onOpenOmnibox}
          onSignOut={onSignOut}
        />
      </aside>

      {/* Mobile top bar — hamburger + brand + search, shown below lg where the rail is hidden. */}
      <header
        className="sticky top-0 flex h-14 items-center gap-2 border-b border-(--border-hairline) bg-(--surface)/85 px-3 backdrop-blur-md lg:hidden"
        style={{ zIndex: "var(--z-sticky)" }}
      >
        <button
          ref={hamburgerRef}
          type="button"
          aria-label={t("shell.openMenu")}
          onClick={() => setDrawerOpen(true)}
          className="grid size-9 place-items-center rounded-(--radius-sm) text-(--text-muted) transition-colors hover:bg-(--surface-hover) hover:text-(--text) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)"
        >
          <Menu className="size-5" aria-hidden />
        </button>
        <BrandMark />
        {onOpenOmnibox && (
          <button
            type="button"
            aria-label={t("shell.openOmnibox")}
            onClick={() => onOpenOmnibox()}
            className="ml-auto grid size-9 place-items-center rounded-(--radius-sm) text-(--text-muted) transition-colors hover:bg-(--surface-hover) hover:text-(--text) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)"
          >
            <Search className="size-5" aria-hidden />
          </button>
        )}
      </header>

      {/* Mobile drawer — the same rail content over a scrim. */}
      {drawerOpen && (
        <div className="lg:hidden">
          <div
            className="fixed inset-0 bg-[oklch(0.2_0.02_265_/_0.5)]"
            style={{ zIndex: "var(--z-modal)" }}
            aria-hidden
            onClick={() => setDrawerOpen(false)}
          />
          <aside
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label={t("nav.primary")}
            className="fixed inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-(--sidebar) shadow-(--shadow-lg) animate-[drawer-in_var(--dur)_var(--ease-out)] motion-reduce:animate-none"
            style={{ zIndex: "var(--z-modal)" }}
          >
            <SidebarContent
              visibleNav={visibleNav}
              onOpenOmnibox={onOpenOmnibox}
              onSignOut={onSignOut}
              onNavigate={() => setDrawerOpen(false)}
              onClose={() => setDrawerOpen(false)}
              closeButtonRef={closeButtonRef}
            />
          </aside>
        </div>
      )}

      {/* Content */}
      <div className="lg:pl-64">
        <main
          id="main"
          tabIndex={-1}
          // r10 22/7: principal feedback ("ขยายให้เต็มหน้าจอ") — dropped the `mx-auto max-w-7xl` column
          // cap that crushed every screen into a centered 1280px strip with dead space on wide monitors.
          // Portal is dashboards/tables end to end (no long-form prose surface), so full-bleed fluid width
          // is correct everywhere; comfortable responsive side padding is all that's kept.
          className="px-4 py-8 focus:outline-none sm:px-6 lg:px-10 lg:py-10"
        >
          {children}
        </main>
      </div>
    </div>
  );
}

/** The brand lockup — an indigo mark + wordmark; identical on the rail and the mobile top bar. */
function BrandMark({ onNavigate }: { onNavigate?: (() => void) | undefined }) {
  const { t } = useTranslation();
  return (
    <NavLink to="/" onClick={onNavigate} className="flex shrink-0 items-center gap-2.5">
      <span
        className="grid size-8 place-items-center rounded-(--radius-sm) bg-(--brand) text-(length:--text-sm) font-bold tracking-tight text-(--text-onbrand) shadow-(--shadow-sm)"
        aria-hidden
      >
        Z
      </span>
      <span className="text-(length:--text-base) font-semibold text-(--text-strong)">
        {t("app.name")}
      </span>
    </NavLink>
  );
}

/**
 * Compact EN/TH segmented toggle, rendered by SidebarContent so it appears in both the desktop rail and
 * the mobile drawer footer. English is the app default (principal decision 2026-07-23); Thai is a
 * first-class, fully-translated alternative — this is how a user reaches it. `setLang` persists the
 * choice, updates i18next (which re-renders every `t()` consumer, including this one), and syncs
 * `<html lang>`.
 */
function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const resolved = i18n.resolvedLanguage ?? i18n.language;
  const current: Lang = (SUPPORTED_LANGS as readonly string[]).includes(resolved)
    ? (resolved as Lang)
    : "en";

  return (
    <div
      role="group"
      aria-label={t("shell.language")}
      className="flex w-fit items-center gap-0.5 rounded-(--radius-sm) bg-(--surface-sunken) p-0.5"
    >
      {SUPPORTED_LANGS.map((lang) => {
        const active = lang === current;
        const label = lang === "th" ? t("lang.th") : t("lang.en");
        return (
          <button
            key={lang}
            type="button"
            aria-pressed={active}
            aria-label={t("lang.switchTo", { lang: label })}
            onClick={() => setLang(lang)}
            className={cn(
              "rounded-(--radius-sm) px-2 py-1 text-(length:--text-2xs) font-semibold uppercase tracking-wide transition-colors " +
                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)",
              active
                ? "bg-(--brand) text-(--text-onbrand) shadow-(--shadow-sm)"
                : "text-(--text-muted) hover:bg-(--surface-hover) hover:text-(--text)",
            )}
          >
            {lang}
          </button>
        );
      })}
    </div>
  );
}

interface SidebarContentProps {
  visibleNav: NavItem[];
  // The `| undefined` is deliberate under exactOptionalPropertyTypes: AppShell forwards its own optional
  // props straight through, so these arrive as an EXPLICIT undefined, not as an absent key. Every one is
  // guarded below (`onOpenOmnibox && …`, `onClose && …`, `onNavigate?.()`), so absent and explicit-undefined
  // behave identically — the honest type is "may be absent OR undefined".
  onOpenOmnibox?: (() => void) | undefined;
  onSignOut?: (() => void) | undefined;
  /** Called after a nav click (drawer dismiss on mobile). */
  onNavigate?: (() => void) | undefined;
  /** Renders a close button (drawer only). */
  onClose?: (() => void) | undefined;
  /** Ref to the close button so the drawer can move focus to it on open. */
  closeButtonRef?: RefObject<HTMLButtonElement | null> | undefined;
}

/** The rail's contents — shared by the fixed desktop rail and the mobile drawer. */
function SidebarContent({
  visibleNav,
  onOpenOmnibox,
  onSignOut,
  onNavigate,
  onClose,
  closeButtonRef,
}: SidebarContentProps) {
  const { t } = useTranslation();
  const { me, role } = useRbac();

  return (
    <div className="flex h-full flex-col px-3 py-4">
      <div className="flex items-center justify-between px-1.5 pb-4">
        <BrandMark onNavigate={onNavigate} />
        {onClose && (
          <button
            ref={closeButtonRef}
            type="button"
            aria-label={t("shell.closeMenu")}
            onClick={onClose}
            className="grid size-8 place-items-center rounded-(--radius-sm) text-(--text-muted) transition-colors hover:bg-(--surface-hover) hover:text-(--text) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand) lg:hidden"
          >
            <X className="size-5" aria-hidden />
          </button>
        )}
      </div>

      {onOpenOmnibox && (
        <button
          type="button"
          onClick={() => {
            onNavigate?.();
            onOpenOmnibox();
          }}
          aria-label={t("shell.openOmnibox")}
          className="mb-2 flex h-9 w-full items-center gap-2.5 rounded-(--radius-sm) border border-(--border) bg-(--surface) px-3 text-left text-(length:--text-sm) text-(--text-placeholder) transition-colors hover:border-(--border-strong) hover:text-(--text-muted) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)"
        >
          <Search className="size-4 shrink-0 text-(--text-subtle)" aria-hidden />
          <span className="truncate">{t("action.search")}</span>
          <kbd className="tabular ml-auto shrink-0 rounded-(--radius-sm) border border-(--border) bg-(--surface-sunken) px-1.5 py-0.5 text-(length:--text-2xs) font-medium text-(--text-subtle)">
            {cmdKLabel()}
          </kbd>
        </button>
      )}

      <nav aria-label={t("nav.primary")} className="flex flex-col gap-0.5">
        {visibleNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2.5 rounded-(--radius-sm) px-3 py-2 text-(length:--text-sm) font-medium transition-colors " +
                  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)",
                isActive
                  ? "bg-(--brand) text-(--text-onbrand) shadow-(--shadow-sm)"
                  : "text-(--text-muted) hover:bg-(--surface-hover) hover:text-(--text)",
              )
            }
          >
            {item.icon}
            {t(item.labelKey)}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto flex flex-col gap-2 border-t border-(--sidebar-border) pt-3">
        <div className="px-1.5">
          <LanguageSwitcher />
        </div>
        <div className="flex items-center gap-2.5 rounded-(--radius-sm) px-3 py-2">
          <span
            className="grid size-8 shrink-0 place-items-center rounded-full bg-(--brand-subtle) text-(length:--text-2xs) font-semibold text-(--brand-strong)"
            aria-hidden
          >
            {(me.name ?? me.email).slice(0, 2).toUpperCase()}
          </span>
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="truncate text-(length:--text-xs) font-medium text-(--text)">
              {me.name ?? me.email}
            </span>
            <span className="truncate text-(length:--text-2xs) text-(--text-muted)">
              {me.name ? me.email : t(`role.${role}`)}
            </span>
          </span>
          {onSignOut && (
            <button
              type="button"
              onClick={onSignOut}
              aria-label={t("shell.signOut")}
              className="ml-auto grid size-8 shrink-0 place-items-center rounded-(--radius-sm) text-(--text-muted) transition-colors hover:bg-(--surface-hover) hover:text-(--text) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)"
            >
              <LogOut className="size-4" aria-hidden />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
